import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import WebsiteCta from "../components/WebsiteCta";
import { publicPageFailure } from "../lib/pageSafety";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function Skeleton() {
  return (
    <div style={{ background: "#000", color: "#fff", minHeight: "100vh" }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 16px" }}>
        <div style={{ width: 220, height: 20, borderRadius: 10, background: "rgba(255,255,255,0.10)" }} />
        <div style={{ marginTop: 18, width: "78%", height: 48, borderRadius: 14, background: "rgba(255,255,255,0.10)" }} />
        <div style={{ marginTop: 12, width: "62%", height: 22, borderRadius: 10, background: "rgba(255,255,255,0.08)" }} />
        <div style={{ marginTop: 22, width: 160, height: 44, borderRadius: 14, background: "rgba(108,74,242,0.35)" }} />
        <div style={{ marginTop: 26, display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {[1, 2, 3].map((x) => (
            <div key={x} style={{ height: 90, borderRadius: 18, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RenderPublic({ branding, schema }) {
  const accent = branding?.accent_color || schema?.theme?.accent || "#6C4AF2";
  const ctaHref = schema?.cta_url;

  const btn = {
    background: accent,
    color: "#000",
    borderRadius: 12,
    padding: "12px 16px",
    fontWeight: 800,
    border: "none",
    cursor: "pointer",
  };

  return (
    <div style={{ background: "#000", color: "#fff", minHeight: "100vh" }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 16px" }}>
        {/* small header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(255,255,255,0.10)" }} />
            <div style={{ fontWeight: 800 }}>{branding?.brand_name || "Website"}</div>
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>Powered by OmniMark HQ</div>
        </div>

        {/* content */}
        <div style={{ marginTop: 22 }}>
          {(schema?.sections || []).map((s, idx) => {
            if (s.type === "hero") {
              return (
                <div key={idx} style={{ padding: "34px 0" }}>
                  <div style={{ display: "inline-flex", gap: 8, alignItems: "center", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 10px", borderRadius: 999, background: "rgba(255,255,255,0.05)", fontSize: 12 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 99, background: accent }} />
                    Built for conversions
                  </div>
                  <h1 style={{ marginTop: 16, fontSize: 44, lineHeight: 1.08, letterSpacing: -0.5 }}>
                    {s.props?.headline}
                  </h1>
                  <p style={{ marginTop: 12, fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,0.72)" }}>
                    {s.props?.subheadline}
                  </p>
                  <div style={{ marginTop: 18 }}>
                    <WebsiteCta href={ctaHref} style={btn}>{s.props?.cta || "Get Started"}</WebsiteCta>
                  </div>
                </div>
              );
            }

            if (s.type === "features" || s.type === "trust") {
              const items = s.props?.items || [];
              return (
                <div key={idx} style={{ padding: "18px 0" }}>
                  <h2 style={{ fontSize: 22, marginBottom: 12 }}>{s.type === "trust" ? "Store details" : "What you get"}</h2>
                  <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                    {items.map((it, j) => (
                      <div key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14 }}>
                        <div style={{ fontWeight: 800 }}>{it.title}</div>
                        {!!it.desc && <div style={{ marginTop: 6, color: "rgba(255,255,255,0.70)", fontSize: 13 }}>{it.desc}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            if (s.type === "testimonials") {
              const items = s.props?.items || [];
              if (!items.length) return null;
              return (
                <div key={idx} style={{ padding: "18px 0" }}>
                  <h2 style={{ fontSize: 22, marginBottom: 12 }}>Testimonials</h2>
                  <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                    {items.map((it, j) => (
                      <div key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14 }}>
                        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.80)" }}>"{it.quote || it.text || ""}"</div>
                        <div style={{ marginTop: 10, fontWeight: 700, fontSize: 13 }}>{it.name || "Customer"}</div>
                        {!!it.title && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.60)" }}>{it.title}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            if (s.type === "faq") {
              const items = s.props?.items || [];
              if (!items.length) return null;
              return (
                <div key={idx} style={{ padding: "18px 0" }}>
                  <h2 style={{ fontSize: 22, marginBottom: 12 }}>FAQ</h2>
                  <div style={{ display: "grid", gap: 10 }}>
                    {items.map((it, j) => (
                      <details key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 14, padding: 12 }}>
                        <summary style={{ cursor: "pointer", fontWeight: 700 }}>{it.q || it.question}</summary>
                        <div style={{ marginTop: 8, color: "rgba(255,255,255,0.72)", fontSize: 13 }}>{it.a || it.answer}</div>
                      </details>
                    ))}
                  </div>
                </div>
              );
            }

            if (s.type === "cta") {
              return (
                <div key={idx} style={{ padding: "26px 0" }}>
                  <div style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 18, padding: 18, background: "rgba(255,255,255,0.05)" }}>
                    <div style={{ fontSize: 20, fontWeight: 900 }}>{s.props?.headline}</div>
                    <div style={{ marginTop: 10 }}>
                      <WebsiteCta href={ctaHref} style={btn}>{s.props?.cta || "Get Started"}</WebsiteCta>
                    </div>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.10)", padding: "18px 0", marginTop: 18, color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
          © 2026 — Page powered by OmniMark HQ
        </div>
      </div>
    </div>
  );
}

export default function PublicWebPage() {
  const { slug } = useParams();
  const [branding, setBranding] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      setLoadError(false);
      setSchema(null);
      setBranding(null);
      try {
        const wRes = await fetch(`${API}/api/public/workspaces/${encodeURIComponent(slug)}`);
        if (!wRes.ok) throw Object.assign(new Error("Page load failed"), { status: wRes.status });
        const w = await wRes.json();

        const pRes = await fetch(`${API}/api/public/workspaces/${encodeURIComponent(slug)}/web-project`);
        if (!pRes.ok) throw Object.assign(new Error("Page load failed"), { status: pRes.status });
        const p = await pRes.json();

        if (!mounted) return;

        const brandingObj = w.branding || {};
        // Normalize keys (your backend stores branding jsonb)
        setBranding({
          brand_name: brandingObj.brand_name,
          accent_color: brandingObj.accent_color,
          logo_url: brandingObj.logo_url,
        });
        setSchema(p.schema);
      } catch (e) {
        if (!mounted) return;
        if (publicPageFailure(e?.status) === "not_found") setNotFound(true);
        else setLoadError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug, retry]);

  if (loading) return <Skeleton />;

  if (loadError) return <main role="alert" style={{ padding: 32 }}>
    <h1>Page temporarily unavailable</h1>
    <p>The page could not be loaded. This does not mean it has been removed.</p>
    <button type="button" onClick={() => setRetry((value) => value + 1)}>Try again</button>
  </main>;

  if (notFound) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#000", color: "#fff", padding: 16 }}>
        <div style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 26, fontWeight: 900 }}>Page not found</div>
          <div style={{ marginTop: 10, color: "rgba(255,255,255,0.70)" }}>
            No published website exists for <code style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4 }}>{slug}</code>.
          </div>
        </div>
      </div>
    );
  }

  return <RenderPublic branding={branding} schema={schema} />;
}
